import { Component, EventEmitter, OnInit, Output, inject, signal, computed, effect, Input, OnDestroy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
//Models
import { GetEMWorkOrderItemsByWorkOrder_Result, GetEMCostCodes_Result } from '@typeDefs/equipment/responses/equipment.d';
import { MechanicFormData, EMWorkOrder, EMWorkOrderItem, EMCostCode, Equipment } from '@shared/model/mechanic-type-data.model';
//Services
import { EmployeeService } from '@pages/employee/services/employee.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-mechanical-dropdown',
  templateUrl: './mechanical-line.component.html',
  styleUrls: ['./mechanical-line.component.css'],
})
export class MechanicalLineDropdownComponent implements OnInit, OnDestroy {
  @Input() kioskPassword: string | null = null;
  @Input() inputWorkOrder: EMWorkOrder | null = null;
  @Input() inputWorkOrderItem: EMWorkOrderItem | null = null;
  @Input() inputEquipment: Equipment | null = null;
  @Input() inputCostCode: EMCostCode | null = null;
  @Input() fromTimeClockDashboard: boolean = false;
  @Output() dropdownChanged = new EventEmitter<MechanicFormData>();
  //services
  private employeeService = inject(EmployeeService);
  private portalSettings$ = inject(PortalSettingsService).portalSettings$;

  portalSettings = toSignal(this.portalSettings$);

  workOrderInput$ = new Subject<string>();
  equipmentInput$ = new Subject<string>();
  workOrderList = signal<EMWorkOrder[]>([]);
  workOrderItemsList = signal<EMWorkOrderItem[]>([]);
  equipmentList = signal<Equipment[]>([]);
  costCodeList = signal<EMCostCode[]>([]);
  workOrderSearchtext = signal<string>('');
  selectedWorkOrder = signal<EMWorkOrder | null>(null);
  selectedWorkOrderItem = signal<EMWorkOrderItem | null>(null);
  selectedEquipment = signal<Equipment | null>(null);
  selectedCostCode = signal<EMCostCode | null>(null);

  hideWorkOrder = computed(() => this.portalSettings().HideWorkOrderInClockIn.ConfigValue === '1' && !this.fromTimeClockDashboard);
  isValidForm = computed(() => {
    const hasEquipmentAndCostCode: boolean = this.selectedEquipment() != null && this.selectedCostCode() != null;
    const hasValidWorkOrderAndItem: boolean =
      (this.selectedWorkOrder() != null && this.selectedWorkOrderItem() != null) ||
      (this.selectedWorkOrder() == null && this.selectedWorkOrderItem() == null);

    return hasEquipmentAndCostCode && hasValidWorkOrderAndItem;
  });

  selectedMechanicalTypeValues = computed<MechanicFormData>(() => ({
    selectedWorkOrder: this.selectedWorkOrder(),
    selectedWorkOrderItem: this.selectedWorkOrderItem(),
    selectedEquipment: this.selectedEquipment(),
    selectedCostCode: this.selectedCostCode(),
    isValidForm: this.isValidForm(),
  }));

  ngOnInit(): void {
    this.registerLookups();
    if (this.fromTimeClockDashboard) 
      this.loadInitialdropdownValues();
  }

   loadInitialdropdownValues():void {
    let emGroup: number | null = null;
    if (this.inputWorkOrder) {
      this.selectedWorkOrder.set(this.inputWorkOrder);
      this.getWorkOrderItems(this.selectedWorkOrder().EMCo, this.selectedWorkOrder().WorkOrder);
    }
    if (this.inputWorkOrderItem) {
      this.selectedWorkOrderItem.set(this.inputWorkOrderItem);
      emGroup = this.selectedWorkOrderItem().EMGroup;
    }
    if (this.inputEquipment) {
      this.selectedEquipment.set(this.inputEquipment);
      emGroup = this.selectedEquipment().EMGroup;

    }
    if (emGroup) {
      this.employeeService.getEMCostCodes(emGroup, this.kioskPassword).subscribe((response: GetEMCostCodes_Result[]) => {
        this.updateList(response, this.costCodeList, cc => ({
          ...cc,
          DisplayValue: `${cc.CostCode} - ${cc.Description}`,
        }));
      });
    }
    if (this.inputCostCode) this.selectedCostCode.set(this.inputCostCode);

  }
  constructor() {
    effect(() => {
      this.dropdownChanged.emit(this.selectedMechanicalTypeValues());
    });
  }

  private registerLookups(): void {
    this.setupWorkOrderLookup();
    this.setupEquipmentLookup();
  }

  private setupWorkOrderLookup(): void {
    this.workOrderInput$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(term =>
          term?.length > 2
            ? this.employeeService.getEMWorkOrders(term, this.kioskPassword).pipe(
              map(response =>
                response.map(wo => ({
                  ...wo,
                  DisplayValue: `${wo.EMCo} - ${wo.WorkOrder} - ${wo.Description}`,
                }))
              )
            )
            : of([])
        )
      )
      .subscribe(workOrders => this.workOrderList.set(workOrders));
  }

  private setupEquipmentLookup(): void {
    this.equipmentInput$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(term =>
          term?.length > 2
            ? this.employeeService
              .getEquipments(
                term,
                this.selectedWorkOrder()?.EMCo,
                this.selectedWorkOrder()?.WorkOrder,
                this.selectedWorkOrderItem()?.WOItem,
                this.kioskPassword
              )
              .pipe(
                map(response =>
                  response.map(eq => ({
                    ...eq,
                    DisplayValue: `${eq.EMCo} - ${eq.Equipment} - ${eq.Description}`,
                  }))
                )
              )
            : of([])
        )
      )
      .subscribe(equipmentList => this.equipmentList.set(equipmentList));
  }

  workOrderChanged(workOrder: EMWorkOrder): void {
    this.selectedWorkOrder.set(workOrder);
    this.resetSelections(['workOrderItem', 'equipment', 'costCode']);
    if (workOrder) {
      this.getWorkOrderItems(workOrder.EMCo, workOrder.WorkOrder);
    }
  }

  async getWorkOrderItems(eMCO: number, workOrder: string): Promise<void> {
    this.employeeService.getEMWorkOrderItems(
      eMCO,
      workOrder,
      this.kioskPassword
    ).subscribe((response: GetEMWorkOrderItemsByWorkOrder_Result[]) => {
      this.updateList(response, this.workOrderItemsList, wo => ({
        ...wo,
        DisplayValue: `${wo.WOItem} - ${wo.Description}`,
      }));
    });
  }

  workOrderItemChanged(workOrderItem: EMWorkOrderItem): void {
    this.selectedWorkOrderItem.set(workOrderItem);
    this.resetSelections(['equipment', 'costCode']);
    this.getEquipmentList('', this.selectedWorkOrder()?.EMCo, this.selectedWorkOrder()?.WorkOrder, workOrderItem.WOItem);
    this.getCostCodes(workOrderItem.EMGroup);
  }

  getEquipmentList(searchtext: string, eMCo: number, workOrder: string, WOItem?: number): void {
    this.employeeService
      .getEquipments(searchtext, eMCo, workOrder, WOItem, this.kioskPassword)
      .pipe(
        map(response =>
          response.map(eq => ({
            ...eq,
            DisplayValue: `${eq.EMCo} - ${eq.Equipment} - ${eq.Description}`,
          }))
        )
      )
      .subscribe(equipmentList => {
        this.selectedEquipment.set(equipmentList?.[0]);
      });
  }

  equipmentChanged(equipment: Equipment): void {
    this.selectedEquipment.set(equipment);

    if (!this.selectedWorkOrderItem()) {
      this.resetSelections(['costCode']);
    }

    if (equipment && (!this.selectedWorkOrderItem() || this.costCodeList()?.length == 0)) {
      this.getCostCodes(equipment.EMGroup);
    }
  }

  async getCostCodes(eMGroup: number): Promise<void> {
    this.employeeService.getEMCostCodes(eMGroup, this.kioskPassword).subscribe((response: GetEMCostCodes_Result[]) => {
      this.updateList(response, this.costCodeList, cc => ({
        ...cc,
        DisplayValue: `${cc.CostCode} - ${cc.Description}`,
      }));
      if (this.selectedWorkOrderItem()) {
        const matchingCostCode = this.costCodeList().find(cc => cc.CostCode === this.selectedWorkOrderItem()?.CostCode) || null;
        this.selectedCostCode.set(matchingCostCode);
      }
    })
  };

  costCodeChanged(costCode: EMCostCode): void {
    this.selectedCostCode.set(costCode);
  }

  resetSelections(selections: string[]): void {
    if (selections.includes('workOrderItem')) {
      this.selectedWorkOrderItem.set(null);
      this.workOrderItemsList.set([]);
    }
    if (selections.includes('equipment')) {
      this.selectedEquipment.set(null);
      this.equipmentList.set([]);
    }
    if (selections.includes('costCode')) {
      this.selectedCostCode.set(null);
      this.costCodeList.set([]);
    }
  }

  updateList<T>(response: T[] | undefined, listSignal: { set: (value: T[]) => void }, mapFn: (item: T) => T): void {
    if (response) {
      listSignal.set(response.map(mapFn));
    }
  }

  ngOnDestroy(): void {
    this.workOrderInput$.unsubscribe();
    this.equipmentInput$.unsubscribe();
  }
}
